import { useVerificationInfo } from '@/features/user-verification/api/queries/useVerificationInfo';
import { useOperationStatusIcons } from '@/shared/api';
import { useRegistrationDictionary } from '@/shared/api/queries/useRegistrationDictionary';
import {
  SUMSUB_VERIFICATION_STATUS,
  VERIFICATION_STEPS,
} from '@/shared/config';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VERIFICATION_STATUS, type VerificationStatus } from './types';
import { getSumsubStatusButton } from '../lib/getSumsubStatusButton';
import { useSessionStore } from '@/entities/session';

interface Props {
  isBlackSuccessIcon?: boolean;
}

const useVerificationStatus = (props?: Props) => {
  const { session } = useSessionStore();
  const { t } = useTranslation();

  const { userVerificationInfo, isVerificationInfoLoading } =
    useVerificationInfo();

  const { operationsStatusIconsData, isOperationStatusIconsLoading } =
    useOperationStatusIcons();

  const {
    pendingIcon,
    rejectedIcon,
    successIcon,
    blackSuccessIcon,
    blackSuccessWithChevIcon,
    blackPendingIcon,
    blackRejectedIcon,
  } = useMemo(() => {
    return {
      pendingIcon: operationsStatusIconsData?.value.find(
        icon => icon.status === 'pending'
      )?.icon,
      rejectedIcon: operationsStatusIconsData?.value.find(
        icon => icon.status === 'rejected'
      )?.icon,
      successIcon: '/assets/check-label.svg',
      blackSuccessIcon: '/assets/check-label-black.svg',
      blackSuccessWithChevIcon: '/assets/check-label-black-with-chev.svg',
      blackPendingIcon: '/assets/check-label-black-with-clock.svg',
      blackRejectedIcon: '/assets/failed-status-icon.svg',
    };
  }, [operationsStatusIconsData]);

  const { registrationDictionaryData, isRegistrationDictionaryLoading } =
    useRegistrationDictionary();

  const isLoading =
    isVerificationInfoLoading ||
    isOperationStatusIconsLoading ||
    isRegistrationDictionaryLoading;

  const verificationStatus: VerificationStatus | null = useMemo(() => {
    if (!userVerificationInfo) {
      return null;
    }

    const isCorporate = session?.additional?.isCorporate;

    const { stepId, statusSumSubId, result } = userVerificationInfo;

    switch (stepId) {
      case VERIFICATION_STEPS.EULA:
      case VERIFICATION_STEPS.PROFILE:
        return VERIFICATION_STATUS.START;
      case VERIFICATION_STEPS.QUESTIONARIES:
        return VERIFICATION_STATUS.PENDING;
      case VERIFICATION_STEPS.SUMSUB:
        return statusSumSubId === SUMSUB_VERIFICATION_STATUS.COMPLETED
          ? VERIFICATION_STATUS.SUMSUB_COMPLETED
          : VERIFICATION_STATUS.SUMSUB_PENDING;
      case VERIFICATION_STEPS.COMPLIANCE_CHECK:
        return VERIFICATION_STATUS.COMPLIANCE_CHECK;
      case VERIFICATION_STEPS.FINISH:
        if (result === 'rejected') return VERIFICATION_STATUS.REJECTED;
        if (isCorporate) return VERIFICATION_STATUS.CORPORATE_SUCCESS;
        return null;
      default:
        return null;
    }
  }, [
    userVerificationInfo,
    isVerificationInfoLoading,
    session?.additional?.isCorporate,
  ]);

  const isVerificationPending =
    verificationStatus === VERIFICATION_STATUS.PENDING ||
    verificationStatus === VERIFICATION_STATUS.SUMSUB_PENDING ||
    verificationStatus === VERIFICATION_STATUS.SUMSUB_COMPLETED ||
    verificationStatus === VERIFICATION_STATUS.COMPLIANCE_CHECK;

  const icon = useMemo(() => {
    if (
      props &&
      props.isBlackSuccessIcon &&
      verificationStatus != VERIFICATION_STATUS.CORPORATE_SUCCESS
    ) {
      if (verificationStatus === VERIFICATION_STATUS.REJECTED) {
        return blackRejectedIcon;
      }

      return isVerificationPending ? blackPendingIcon : blackSuccessIcon;
    }

    switch (verificationStatus) {
      case VERIFICATION_STATUS.START:
        return successIcon;
      case VERIFICATION_STATUS.PENDING:
      case VERIFICATION_STATUS.SUMSUB_PENDING:
      case VERIFICATION_STATUS.SUMSUB_COMPLETED:
      case VERIFICATION_STATUS.COMPLIANCE_CHECK:
        return pendingIcon;
      case VERIFICATION_STATUS.REJECTED:
        return rejectedIcon;
      case VERIFICATION_STATUS.CORPORATE_SUCCESS:
        return blackSuccessWithChevIcon;
      default:
        return successIcon;
    }
  }, [verificationStatus, props?.isBlackSuccessIcon]);

  const title = useMemo(() => {
    switch (verificationStatus) {
      case VERIFICATION_STATUS.START:
        return t('personalAccount.verificationCta.title');
      case VERIFICATION_STATUS.PENDING:
      case VERIFICATION_STATUS.SUMSUB_PENDING:
      case VERIFICATION_STATUS.SUMSUB_COMPLETED:
      case VERIFICATION_STATUS.COMPLIANCE_CHECK:
        return t('personalAccount.verificationCta.processTitle');
      case VERIFICATION_STATUS.REJECTED:
        return t('personalAccount.verificationCta.completedTitle');
      case VERIFICATION_STATUS.CORPORATE_SUCCESS:
        return t('personalAccount.verificationCta.corporateSuccessTitle');
      default:
        return t('personalAccount.verificationCta.title');
    }
  }, [verificationStatus, t]);

  const description = useMemo(() => {
    switch (verificationStatus) {
      case VERIFICATION_STATUS.START:
        return t('personalAccount.verificationCta.description');
      case VERIFICATION_STATUS.PENDING:
        return t('personalAccount.verificationCta.processDescription');
      case VERIFICATION_STATUS.SUMSUB_PENDING:
        return '';
      case VERIFICATION_STATUS.SUMSUB_COMPLETED:
      case VERIFICATION_STATUS.COMPLIANCE_CHECK:
        return t('personalAccount.verificationCta.complianceCheckDescription');
      case VERIFICATION_STATUS.REJECTED:
        return t('personalAccount.verificationCta.rejectedDescription');
      case VERIFICATION_STATUS.CORPORATE_SUCCESS:
        return t('personalAccount.verificationCta.corporateSuccessDescription');
    }
  }, [verificationStatus, t]);

  const buttonText = useMemo(() => {
    switch (verificationStatus) {
      case VERIFICATION_STATUS.START:
        return t('personalAccount.verificationCta.button');
      case VERIFICATION_STATUS.PENDING:
        return t('personalAccount.verificationCta.buttonPending');
      case VERIFICATION_STATUS.SUMSUB_PENDING:
        return (
          getSumsubStatusButton(
            registrationDictionaryData?.value,
            userVerificationInfo?.statusSumSubId || ''
          ) || t('personalAccount.verificationCta.buttonPending')
        );
      case VERIFICATION_STATUS.SUMSUB_COMPLETED:
      case VERIFICATION_STATUS.COMPLIANCE_CHECK:
        return '';
      case VERIFICATION_STATUS.CORPORATE_SUCCESS:
      case VERIFICATION_STATUS.REJECTED:
        return t('personalAccount.verificationCta.rejectedButton');
      default:
        return '';
    }
  }, [verificationStatus, t]);

  return {
    icon,
    title,
    description,
    buttonText,
    isLoading,
    isVerificationRejected: verificationStatus === VERIFICATION_STATUS.REJECTED,
    isCorporateSuccess:
      verificationStatus === VERIFICATION_STATUS.CORPORATE_SUCCESS,
    isVerificationStart:
      verificationStatus === VERIFICATION_STATUS.START ||
      verificationStatus === null,
    isSumsubPending: verificationStatus === VERIFICATION_STATUS.SUMSUB_PENDING,
    isVerificationAwaiting:
      verificationStatus === VERIFICATION_STATUS.SUMSUB_COMPLETED ||
      verificationStatus === VERIFICATION_STATUS.COMPLIANCE_CHECK,
  };
};

export { useVerificationStatus };
